import type { Log } from './types'

import pipe from 'lodash/fp/pipe'

import debugLog from '@app/utils/debug-log'
import env from '@app/utils/environment'

const logToConsoleIfDev = (log: Log): Log => {
  if (!env.__DEV__ || env.__TEST__) {
    return log
  }

  if (log.level === 'error') {
    console.error(log.message, log.error)
  } else if (log.level === 'warning') {
    console.warn(log.message, log.extra)
  } else {
    console.log(log.message, log.extra)
  }

  return log
}

const logToDebug = (log: Log): Log => {
  debugLog(log.message, log.error, log.extra)
  return log
}

/**
 * Avoid logging network errors to Sentry
 */
const flushNetworkErrors = (log: Log): Log | null => {
  const { error, extra } = log

  // Filter out "Failed to fetch" errors since those are connectivity errors
  // and provide little information in sentry
  if (
    extra &&
    extra.error instanceof TypeError &&
    extra.error.message === 'Failed to fetch'
  ) {
    return null
  }

  if (errorContainsStatus(error)) {
    if (error.status === 0 || (error.status >= 400 && error.status < 500)) {
      return null
    }
  }

  // An error can also be attached as extra info to a warning or info log
  if (typeof extra === 'object' && errorContainsStatus(extra.error)) {
    if (
      extra.error.status === 0 ||
      (extra.error.status >= 400 && extra.error.status < 500)
    ) {
      return null
    }
  }

  return log
}

function errorContainsStatus(err: unknown): err is { status: number } {
  return typeof err === 'object' && err !== null && 'status' in err
}

const logToSentry = (log: Log | null): Log | null => {
  if (!window.Sentry || !log) {
    return log
  }

  const { level, message, error, extra } = log

  if (level === 'error') {
    window.Sentry.captureException(error, extra ? { extra } : undefined)
  } else {
    window.Sentry.captureMessage(message, {
      level,
      ...(extra && { extra }),
    })
  }

  return log
}

const SfLogInternal = (
  message: string,
  options: Omit<Log, 'message'>
): Log | null =>
  pipe(
    logToDebug,
    logToConsoleIfDev,
    flushNetworkErrors,
    logToSentry
  )({
    ...options,
    message,
  })

/**
 * An adapter for logging client side. Logs to console, debugLog, and Sentry, depending on the environment.
 */
export const SfLog = {
  error: (
    message: string,
    options: Omit<Log, 'message' | 'level'> = {}
  ): Log | null => SfLogInternal(message, { ...options, level: 'error' }),
  info: (
    message: string,
    options: Omit<Log, 'message' | 'level'> = {}
  ): Log | null => SfLogInternal(message, { ...options, level: 'info' }),
  warn: (
    message: string,
    options: Omit<Log, 'message' | 'level'> = {}
  ): Log | null => SfLogInternal(message, { ...options, level: 'warning' }),
}
